/**
 * 03 报名表单 `/enroll/:registrationId`
 * -----------------------------------------------------------------------------
 * 先进一道**同意闸门**，再走三段式表单：
 *   前 置 屏 —— 《报名须知》《个人信息授权书》正文 + 勾选 + 「同意并开始填写」
 *   ① 基本信息     —— 全部字段（基本 / 报考与职业 / 报名材料）+ 教材邮寄地址
 *   ② 身份核验     —— 三件"器械"：身份证拍照 OCR / 证件照现拍 / 短信验证
 *   ③ 确认提交     —— **信息汇总** + 承诺勾选 + 手写签名 + 提交
 *
 * 同意闸门为什么独立成一屏（2026-10-18 三次裁决）：
 *   授权必须**早于采集** —— 第 3 步就要拍身份证与人脸，把「我已阅读并同意」摆在
 *   第 1 步页脚，等于让学员在什么都没看到时就先签字；摆到最后一步，则是采集完
 *   才补授权，顺序反了。所以它不占步骤条的格子，而是进入表单前的一屏。
 *   一期没有独立的条款页，正文直接写在这一屏（不做点不开的假链接）。
 *
 * 分页依据（2026-10-21 改版，按**谁来产出这个值**切，不按字段多少切）：
 *   - 靠「举手机对着拍 / 收一条短信」产出的，一律进第 2 步（`KycFlow`）——
 *     它们的失败方式都是当场重拍重填，混在长表单里会打断填写节奏；
 *   - 靠「敲字 / 选选项 / 选文件」产出的，全部留在第 1 步。**不再按后台分组切页**：
 *     分组是后台维护的表单结构，把它当步骤会得出「材料与承诺」这种既不是一组
 *     字段、也不对应一个动作的步骤名（2026-10-21 用户把这步拆掉了）；
 *   - 「承诺 + 签名」是**签署**动作，只能在学员看完全部填写内容之后发生，
 *     所以它跟确认页走 —— 签的就是屏幕上这一版信息。
 *
 * 于是第 1 步 = 全部字段 − 核验字段 − 承诺 − 签名。后台新加字段默认落第 1 步，
 * 不会因为「组没登记」而凭空消失（旧版按组切页有这个坑）。
 *
 * 提交顺序刻意固定：先落地址 → 再存草稿 → 最后提交。
 * 这样服务端在提交那一刻看到的是**完整且一致**的一份数据。
 *
 * ⚠️ 这里**没有「领取方式」二选一**（2026-10-18 用户裁决）：教材怎么到手是
 * **发放环节**的事 —— 一期以邮寄为主（**运费到付**、系统不结算运费、地址可自助改），
 * 现场领取只在后台做台账，`deliveryMethod` 建单就是 `MAIL`。报名时让学员选，
 * 等于让他在还没录取的时候先替我们决定物流；而且一旦选「现场领取」，连地址都
 * 不用填了，反而留个坑。所以前端只呈现一个只读事实（`needAddress` 由服务端
 * `deliveryMethod` 决定），学员只需把「寄到哪儿」写清楚。
 *
 * ⚠️ 确认页**不出现金额、也不出现收件人**（2026-10-21 用户裁决）：
 *   - 费用明细移到缴费功能 —— 报名只是信息填写，金额在缴费页出现一次就够；
 *   - 收件人 / 手机号在**领取教材**时由「报名人姓名 + 注册手机号」自动带入、可改，
 *     报名阶段只落一个地址，免得同一个人同一份信息在两处各填一遍。
 */
import {
  type AnswerError,
  CN_MOBILE_PATTERN,
  isRegionComplete,
  isRegionDetailFilled,
  type KycStep,
} from '@enroll/shared';
import { Dialog, Toast } from 'antd-mobile';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { ApiError } from '../api/client';
import { describeApiError, fieldErrorsOf, kycStepOf } from '../api/errors';
import { saveAddress, saveDraft, submitRegistration } from '../api/h5';
import { useFormSchema, useKycSteps, useProfileBasic, useRegistration } from '../api/hooks';
import type { H5ProfileBasic, ShippingAddressInput } from '../api/types';
import { AnswerSummary, PromiseList, SignatureBlock } from '../components/EnrollConfirm';
import { isPromiseField, isSignatureField } from '../components/EnrollConfirm.logic';
import { FormRenderer } from '../components/FormRenderer/FormRenderer';
import {
  type Answers,
  collectErrors,
  collectFieldErrors,
  isFieldShown,
  knownFieldKeys,
  ownFieldErrors,
  prunedAnswers,
  RESIDENT_ID_TYPE,
  resolveKycFields,
} from '../components/FormRenderer/logic';
import { RegionField } from '../components/FormRenderer/RegionField';
import { KycFlow, type KycFlowHandle } from '../components/KycFlow/KycFlow';
import {
  Button,
  Card,
  ErrorBlock,
  LoadingBlock,
  Notice,
  PageShell,
  StepDots,
} from '../components/layout';
import {
  addressHasContent,
  findWorkUnitRegionField,
  hasCopyableRegion,
  regionToAddressPatch,
} from '../lib/address-reuse';
import { createSaveQueue, type SaveQueue, type SaveState } from '../lib/autosave';
import { leaveTopGuard, pushBackGuard, triggerTopGuard } from '../lib/back-guard';
import {
  clearLocalAddress,
  readLocalAddress,
  readLocalDraft,
  saveLocalAddress,
  useDraftStore,
} from '../store/draft';
import { rememberRegistration } from '../store/recent';

const FORM_STEPS = ['基本信息', '身份核验', '确认提交'] as const;
/** 设计稿里这一整条流程共用同一个标题（分步靠顶部圆点表达，标题不跟着变） */
const PAGE_TITLE = '填写报名信息';

/**
 * 用账号资料预填姓名 / 手机号（2026-10-31）。
 *
 * 三条约束，都是为了不把「预填」做成「覆盖」：
 *   1. **只填空字段** —— 草稿里已有值一律不动。代报场景下学员会主动改成被代报人的
 *      信息，预填要是覆盖过去，等于每次进来都把别人的单改成自己的。
 *   2. **无变化返回原对象** —— `answers` 引用不变，React 不重渲染，自动保存也不会
 *      因为一次预填多发一个请求。
 *   3. **手机号用明文**（`profile.phone`），不能用 `phoneMasked` —— 掩码值当表单值
 *      存进去会被格式校验拦下，还会把 `PHONE-005` 的号码比对弄脏。
 */
function prefillFromProfile(
  answers: Record<string, unknown>,
  fields: readonly { key: string; type: string; system?: boolean }[],
  profile: H5ProfileBasic | undefined,
): Record<string, unknown> {
  if (!profile) return answers;
  const blank = (value: unknown) => value === undefined || value === null || value === '';

  let changed = false;
  const next = { ...answers };

  // 姓名字段与 `registration.pii` 抽取 `applicantName` 的口径一致：system + text
  const nameField = fields.find(
    (field) => field.system === true && (field.type === 'text' || field.type === 'textarea'),
  );
  if (nameField && blank(next[nameField.key]) && profile.realName) {
    next[nameField.key] = profile.realName;
    changed = true;
  }

  const phoneField = fields.find((field) => field.type === 'phone');
  if (phoneField && blank(next[phoneField.key]) && profile.phone) {
    next[phoneField.key] = profile.phone;
    changed = true;
  }

  return changed ? next : answers;
}

/**
 * 进入表单前的《报名须知》《个人信息授权书》要点。
 *
 * ⚠️ 这两份是**摘要**，不是法律全文 —— 一期没有独立的条款页（画布上有勾选框、
 * 没有条款正文），所以把「学员必须知道且我们已经做到」的事实列清楚：
 * 只写**已实现的口径**（缴费靠备注码、字段三档可编辑性、驳回重提、教材邮寄、
 * 采集范围与用途、审核留痕、可联系客服更正），不写尚未落地的留存期限承诺
 * （那是服务端保留策略的事，写成对外条款就成了空头承诺）。
 * 正式文稿（法务版）到位后，直接替换这两个数组即可，页面结构不用动。
 */
const ENROLL_NOTICE = [
  '提交后报名单进入待缴费，请在截止时间前按缴费指引转账，并在转账备注中填写系统生成的备注码。',
  '部分信息提交后不可修改（标有锁形提示的字段）；部分信息修改后需重新审核，请如实填写。',
  '材料或信息不符合要求的，报名单会被驳回，请按驳回原因修改后重新提交。',
  '报名成功后教材按邮寄发放，请填写可以长期收件的地址。',
] as const;

const PRIVACY_NOTICE = [
  '为完成报名与身份核验，需要采集你的姓名、手机号、身份证号、身份证影像、人脸影像与学历证照。',
  '上述信息仅用于报名审核、身份核验、证书制作与发放联络，不用于其他用途。',
  '影像资料仅授权人员在审核时查看，查看行为记录到具体账号。',
  '你可以通过「联系客服」查询或更正报名信息。',
] as const;

/** 须知页的正文列表（两块共用，省得写两遍圆点结构） */
function NoticeList({ items }: { items: readonly string[] }) {
  return (
    <ul className="bullet-list">
      {items.map((item) => (
        <li key={item} className="bullet-list__item">
          <span className="bullet-list__dot" aria-hidden="true" />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

/**
 * 收件地址状态。
 *
 * 区域是**三级行政区划码**（D-05 残留收口，2026-10-16），与表单 `region` 字段的
 * `RegionValue` 同形状 —— 因此这里能直接喂给 `RegionField` / `isRegionComplete` 判定，
 * 也与服务端 `ShippingAddressDto` 一一对应（直接用它的类型，免得两处漂移）。
 */
type AddressState = ShippingAddressInput;

const EMPTY_ADDRESS: AddressState = {
  receiverName: '',
  phone: '',
  provinceCode: '',
  cityCode: '',
  districtCode: '',
  detail: '',
};

/**
 * 学员在报名阶段能看到的地址校验：**只有地址本身**。
 *
 * 收件人 / 手机号不再让学员填（2026-10-21）—— 它们就是报名人姓名与注册手机号，
 * 在领取教材时自动带入、可改。报名阶段重复问一遍，等于让同一个人把同一份信息
 * 写两次，而且两次不一致时以哪份为准没有答案。
 */
function validateAddress(address: AddressState): AnswerError[] {
  const errors: AnswerError[] = [];
  // 区域完整性用与三端同一份判定：不设区的市只要求到市级（东莞 / 中山 等），
  // 一刀切要求三级齐全会让这些地方的学员永远填不完（见 `region.ts`）。
  if (!isRegionComplete(address)) {
    errors.push({ key: 'provinceCode', message: '请选择完整的省 / 市 / 区' });
  } else if (!isRegionDetailFilled(address)) {
    errors.push({ key: 'detail', message: '请填写详细地址' });
  }
  return errors;
}

/**
 * 发请求前的**完整**校验 —— 服务端 `ShippingAddressDto` 六字段全必填。
 *
 * 收件人 / 手机号从报名信息里补（`addressPayload`），补不上说明姓名还没填、
 * 或手机号还没验证：此时只落本地、不发请求，等它们齐了由 effect 自动补发。
 * 这类「暂存」不是 bug —— 服务端存的必须是完整地址，半份地址它一概 400。
 */
function validateAddressPayload(address: AddressState): AnswerError[] {
  const errors = validateAddress(address);
  if (!address.receiverName.trim()) {
    errors.push({
      key: 'receiverName',
      message: '收件人姓名还没确认，请在「身份核验」步骤补全姓名',
    });
  }
  if (!CN_MOBILE_PATTERN.test(address.phone.trim())) {
    errors.push({
      key: 'phone',
      message: '收件人手机号还没确认，请在「身份核验」步骤完成手机号验证',
    });
  }
  return errors;
}

/** 保存时间按「时:分」显示就够了（同一天的填写，精确到秒没有意义且更吵） */
function formatSavedAt(at: number): string {
  const date = new Date(at);
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/**
 * 页头的保存状态。
 *
 * 取代原来那颗「存草稿」小字按钮 —— 自动保存之后，「要不要存」不再是一个需要学员
 * 决策的问题，他需要知道的是「存上没有」。所以这里只表达三件事：
 *   保存中… / 已保存 18:42 / 未同步 · 重试
 *
 * `error` 做成可点的（其余是纯文本）：弱网下自动保存会静默失败，
 * 给一个显式的重试入口，比让他猜「到底存了没有」有用。
 */
function SaveIndicator({
  state,
  at,
  onRetry,
}: {
  state: SaveState;
  at: number | null;
  onRetry: () => void;
}) {
  if (state === 'saving') {
    return (
      <span className="save-state" aria-live="polite">
        保存中…
      </span>
    );
  }
  if (state === 'error') {
    return (
      <button type="button" className="save-state save-state--error" onClick={onRetry}>
        未同步 · 重试
      </button>
    );
  }
  if (state === 'pending') {
    return <span className="save-state">待保存</span>;
  }
  if (state === 'saved' && at !== null) {
    return <span className="save-state">已保存 {formatSavedAt(at)}</span>;
  }
  return null;
}

export default function EnrollForm() {
  const { registrationId } = useParams<{ registrationId: string }>();
  const navigate = useNavigate();
  const detail = useRegistration(registrationId);
  const schema = useFormSchema(detail.data?.enrollTypeId);
  /** 账号资料：只用于首次填空（见 `prefillFromProfile`），不参与任何校验 */
  const profile = useProfileBasic();

  const [step, setStep] = useState(0);
  /**
   * 已到达过的最远步骤。
   *
   * 步骤条只允许点回「到过的」格子：到过的步骤一定校验通过过，回去改是安全的；
   * 没到过的一跳就是绕过必填校验（直接跳到确认页交一份半成品）。
   * 所以可点性看「到没过」，不看「当前第几步」—— 从确认页退回第 1 步改个字，
   * 「确认提交」那一格必须还能点回去，否则改完就再也回不去了。
   */
  const [maxStep, setMaxStep] = useState(0);
  const [answers, setAnswers] = useState<Answers | null>(null);
  const [errors, setErrors] = useState<AnswerError[]>([]);
  const [kycPassed, setKycPassed] = useState(false);
  /**
   * 姓名兜底值：schema 没配「姓名」字段（`kycNameField` 为空）时，核验卡上的
   * 姓名输入框仍然存在且可用（`IdCardField` 不依赖 nameField 渲染），OCR / 手输
   * 的姓名会照常随 `onChange` 回传 —— 这里不接住的话该值会被静默丢弃，
   * 连锁后果：输入框打不进字 + 收件人姓名永远为空 + 提交被地址校验拦死。
   */
  const [kycNameFallback, setKycNameFallback] = useState('');
  /**
   * 《报名须知》《个人信息授权书》的同意闸门（2026-10-18 三次裁决）。
   *
   * 它**不在步骤条里**，而是进入表单前的独立一屏：授权必须早于采集 ——
   * 第 3 步就要拍身份证与人脸，把「我已阅读并同意」摆在第 1 步页脚，
   * 等于让人在什么都没看到时就先签了字；摆到最后一步，则是采集完才补授权。
   */
  const [consent, setConsent] = useState(false);
  /** 须知页的勾选框（点了「同意并开始填写」后不再是门槛） */
  const [agreed, setAgreed] = useState(false);
  /**
   * 续填落点是否已结算过。
   *
   * 只在**首次拿到完整草稿**时算一次：`answers` 与 `schema` 都是异步到位，
   * 每轮都跟着重算会把学员自己切步骤的动作覆盖回去。
   */
  const [resumed, setResumed] = useState(false);
  const kycRef = useRef<KycFlowHandle>(null);
  /** 核验被拦下时的落点与提示：直接带学员到**要重做的那一步**（KYC-501 的 failCode 反查） */
  const [kycRedo, setKycRedo] = useState<{ step: KycStep; notice: string } | null>(null);
  const [address, setAddress] = useState<AddressState>(EMPTY_ADDRESS);
  const [busy, setBusy] = useState(false);
  /** 表单 / 地址两条保存链路各自的状态（页头合并展示，取「更差」的那个） */
  const [formSave, setFormSave] = useState<{ state: SaveState; at: number | null }>({
    state: 'idle',
    at: null,
  });
  const [addressSave, setAddressSave] = useState<{ state: SaveState; at: number | null }>({
    state: 'idle',
    at: null,
  });
  /**
   * 是否已经建立过「已保存基线」。
   *
   * 自动保存最怕的不是漏存，而是**刚进页面就存一次**（把服务端数据原样写回去，
   * 白白 version+1）。所以第一次拿到 schema + 草稿时只登记基线，不发请求。
   */
  const formBaselineRef = useRef(false);
  /** 地址是否已经从「服务端草稿 + 本地兜底」回填过（回填那一下不该触发保存） */
  const addressHydratedRef = useRef(false);

  const saveLocalDraft = useDraftStore((state) => state.save);
  // 地址的本地兜底是**独立存储**（localStorage，见 store/draft.ts 文件头），
  // 不走 store 的 set：它要能在关掉页面重进后仍然读得到
  const clearLocalAnswers = useDraftStore((state) => state.clearAnswers);
  const clearLocalDraft = useDraftStore((state) => state.clear);

  /**
   * 表单自动保存（服务端）。
   *
   * 节奏是「停手 1.5 秒存一次」而不是「每个字符存一次」：本地那一层已经做到
   * 「填一个字段存一个」（零延迟、不走网络），服务端这一层的作用是**跨会话 / 跨设备
   * 能恢复**，没必要为一串连续输入发一堆 `PATCH` —— 那会撞乐观锁、也会在弱网下堆积。
   */
  const formQueue = useMemo<SaveQueue<Answers> | null>(() => {
    if (!registrationId) return null;
    return createSaveQueue<Answers>({
      delayMs: 1500,
      save: async (value) => {
        await saveDraft(registrationId, value);
        // 服务端已经是权威副本，本地兜底撤掉；否则下次进页面「本地优先」会用旧值盖新值
        clearLocalAnswers(registrationId);
      },
      onChange: (state, at) => setFormSave({ state, at }),
    });
  }, [registrationId, clearLocalAnswers]);

  /**
   * 收件地址自动保存。
   *
   * 比表单慢半档（800ms）：地址是「选完省市区再敲门牌」，中途不完整时服务端会 400。
   * 不完整就**不发**（`save` 里直接返回）—— 但本地每次变化都存过，所以照样不丢。
   *
   * 队列里推的是**补齐后的完整地址**（收件人 / 手机号从报名信息补），
   * 而不是学员能看到的那个「只有地址」的半份 —— 服务端六字段全必填。
   */
  const addressQueue = useMemo<SaveQueue<AddressState> | null>(() => {
    if (!registrationId) return null;
    return createSaveQueue<AddressState>({
      delayMs: 800,
      save: async (value) => {
        if (validateAddressPayload(value).length > 0) return;
        await saveAddress(registrationId, value);
        clearLocalAddress(registrationId);
      },
      onChange: (state, at) => setAddressSave({ state, at }),
    });
  }, [registrationId]);

  // 草稿能力是「进页面就生效」的一组副作用，卸载时把定时器收掉
  useEffect(() => {
    return () => {
      formQueue?.dispose();
      addressQueue?.dispose();
    };
  }, [formQueue, addressQueue]);

  // 换单（同一路由不同 id）时基线要重建，否则会拿上一张单的基线做去重
  // biome-ignore lint/correctness/useExhaustiveDependencies: 这里要的正是「id 变化时重置 ref」，effect 体内刻意不读它
  useEffect(() => {
    formBaselineRef.current = false;
    addressHydratedRef.current = false;
  }, [registrationId]);

  // 初次加载：服务端草稿 + 本地兜底草稿（本地优先，因为它是「没同步成功的那一份」）
  useEffect(() => {
    const loaded = detail.data;
    if (!loaded) return;
    const local = readLocalDraft(loaded.id);
    setAnswers({ ...loaded.formData, ...(local ?? {}) });
  }, [detail.data]);

  /**
   * 账号资料补预填（2026-10-31）。
   *
   * 单独一个 effect，**不并进上面那个**：`detail` / `schema` / `profile` 三条 query
   * 谁先到不确定，并进去就会「先到的被后到的覆盖掉」。
   *
   * 这里只填空字段、且无变化返回原引用，所以重复触发是安全的：既不会覆盖学员已填的
   * 内容（代报场景会主动改成别人的），也不会因为一次预填多触发一次自动保存。
   */
  useEffect(() => {
    const fieldDefs = schema.data?.fields;
    if (!fieldDefs) return;
    setAnswers((prev) => (prev ? prefillFromProfile(prev, fieldDefs, profile.data) : prev));
  }, [schema.data, profile.data]);

  const fields = schema.data?.fields ?? [];
  /**
   * 搬到第 3 步（身份核验）的字段：身份证 / 证件类型 / 手机号 / 姓名。
   * 表单里不渲染它们，但取值照旧写进同一份答案。
   */
  const kyc = useMemo(() => resolveKycFields(fields), [fields]);
  /**
   * 核验状态 —— 确认页只用到其中的 `photoUrl`（职业证件照缩略图）。
   *
   * 为什么要单独拉：**证件照不是表单字段**。它由 ③ 在线生成、挂在 `Asset` 上，
   * 表单答案里从来没有这一项，`AnswerSummary` 遍历字段永远查不到它。
   * 只在进到确认页时才请求：第 ② 步的 `KycFlow` 自己会拉一次核验状态，
   * 提前拉等于同一份数据打两遍。
   */
  const kycStepsQuery = useKycSteps(registrationId, step === 2);
  const kycKeySet = useMemo(() => new Set(kyc.keys), [kyc.keys]);
  /**
   * 清掉「字段表里已经不存在的答案」（2026-11-01）。
   *
   * 脏键的来源是**表单改版**：营养指导员 v5 删掉了 `salesStaff` / `channelSource`
   * 两个字段，14 张老报名单的 `formData` 里还留着它们。服务端保存与提交都不校验
   * 未知键，所以它一直安静地待着，直到学员点第 1 步的「下一步」——
   * `validateAnswers` 给这种键报「表单中不存在该字段」，界面上却没有任何对应输入框，
   * 学员既改不了也看不懂，只被一句指不到控件的 toast 卡死。
   *
   * 两道处理各管一件事：
   *   - **不拦人**：`ownFieldErrors` 在校验处把这些结论丢掉（见 `goNextFromForm`）；
   *   - **不留痕**：就是这里的清理。脏键其实发不出去（`payload` 走 `prunedAnswers`），
   *     所以这不是数据问题、是**界面问题** —— `consentDone` / `resumeAnchorKey` /
   *     `stepOneDone` 都靠遍历 `answers` 判断「有没有填过」，多出来的旧键会让它们
   *     得出与实际不符的结论。入口处清一次，后面全都不必各自防守。
   *
   * **空字段表必须早退**：Schema 还没到（或异常为空）时 `knownKeySet` 是空集，
   * 此时清理等于把整份答案当成「全都不认识」删光 —— 那是比报错严重得多的事故。
   */
  const knownKeySet = useMemo(() => knownFieldKeys(fields), [fields]);
  useEffect(() => {
    if (knownKeySet.size === 0) return;
    setAnswers((prev) => {
      if (!prev) return prev;
      const stale = Object.keys(prev).filter((key) => !knownKeySet.has(key));
      if (stale.length === 0) return prev;
      const next = { ...prev };
      for (const key of stale) delete next[key];
      return next;
    });
  }, [knownKeySet]);

  /** 拆成局部常量：闭包里 TS 无法沿用 `kyc.idField` 的非空收窄 */
  const kycIdField = kyc.idField;
  const kycPhoneField = kyc.phoneField;
  const kycNameField = kyc.nameField;
  /** 证件类型跟着身份证卡走，不再单独占第 1 步一行 */
  const kycTypeField = kyc.idTypeField;
  /**
   * 性别 / 民族也搬到了身份证卡（2026-10-21 用户裁定）：证件上印着，没必要再填一遍。
   * 两者都是**可编辑**的 —— OCR 读不出民族时那是学员唯一的入口。
   */
  const kycGenderField = kyc.genderField;
  const kycEthnicityField = kyc.ethnicityField;

  /**
   * 确认页要渲染的两类字段（承诺 / 签名）。
   *
   * 它们**不参与第 1 步渲染**，所以第 1 步的 `omitKeys` 要把它们连同核验字段一起排掉。
   * 判定见 `isPromiseField` / `isSignatureField`：按类型 + 分组，不写死字段 key。
   */
  const promiseFields = useMemo(() => fields.filter(isPromiseField), [fields]);
  const signatureFields = useMemo(() => fields.filter(isSignatureField), [fields]);
  const promiseKeySet = useMemo(
    () => new Set(promiseFields.map((field) => field.key)),
    [promiseFields],
  );
  const signatureKeySet = useMemo(
    () => new Set(signatureFields.map((field) => field.key)),
    [signatureFields],
  );
  /** 第 1 步不渲染的字段：核验四个 + 承诺 + 签名（都不在第 1 步的输入框里） */
  const stepOneOmitKeys = useMemo(
    () => [...kyc.keys, ...promiseFields.map((f) => f.key), ...signatureFields.map((f) => f.key)],
    [kyc.keys, promiseFields, signatureFields],
  );

  /**
   * 这条单子是不是「回来接着填」的 —— 判据只看草稿里有没有实际内容。
   * -----------------------------------------------------------------------------
   * 不新增服务端字段，也不写本地标记，因为这里有一条必然关系可用：
   * **没点过「同意并开始填写」就进不了表单，进不了表单就不可能有任何答案。**
   * 所以「有答案」⇒「同意过」，换设备、清缓存、换浏览器都成立。
   *
   * 驳回单（`REJECTED`）另算：它是在提交流程里被打回来的，比草稿更靠后，必然同意过，
   * 哪怕答案被清空也一样 —— 否则重新提交前会再让人读一遍须知，纯属打断。
   * 补交单同理（2026-10-27）：它连缴费阶段都到了，授权早就给过。
   *
   * 为什么不直接用「`registrationId` 存在」：单子是入口页 `EnrollNew` 点
   * 「开始填写报名信息」时就建好的，此刻学员一步都还没填、须知也还没看。
   * 拿单子的存在当同意，等于把授权闸门整个删掉 —— 那正是这套闸门要防的。
   */
  const consentDone = useMemo(() => {
    if (!detail.data) return false;
    // 非草稿 = 提交过 = 同意过。含驳回态与补交态（补交不改状态，仍在 PENDING_PAYMENT）
    if (detail.data.status !== 'DRAFT') return true;
    return Object.values(answers ?? {}).some((value) => {
      if (value === null || value === undefined) return false;
      if (typeof value === 'string') return value.trim() !== '';
      if (Array.isArray(value)) return value.length > 0;
      return true;
    });
  }, [detail.data, answers]);

  /**
   * 确认页的汇总分组：全部字段（含核验字段）按后台分组排列，**排除承诺与签名**
   * —— 它们就在汇总下面，作为可编辑的签署区出现，重复列一遍只会让人以为要填两次。
   * 条件隐藏的字段按 `isFieldShown` 过滤，与表单实际看到的保持一致。
   */
  const summaryGroups = useMemo(() => {
    const skip = new Set([...promiseKeySet, ...signatureKeySet]);
    return [...(schema.data?.groups ?? [])]
      .sort((a, b) => a.order - b.order)
      .map((group) => ({
        key: group.key,
        label: group.label,
        fields: fields
          .filter((field) => field.group === group.key && !skip.has(field.key))
          .sort((a, b) => a.order - b.order)
          .filter((field) => isFieldShown(field, answers ?? {})),
      }))
      .filter((group) => group.fields.length > 0);
  }, [schema.data, fields, promiseKeySet, signatureKeySet, answers]);

  /** 字段 key → 该回哪一步（服务端 `ENROLL-004` 的字段错误按它落点） */
  const stepOfKey = useCallback(
    (key?: string): number => {
      if (!key) return 0;
      if (kycKeySet.has(key)) return 1;
      // 承诺 / 签名都住在确认页 —— 它们的错误不能把人送回第 1 步（那里没有输入框）
      if (promiseKeySet.has(key) || signatureKeySet.has(key)) return 2;
      return 0;
    },
    [kycKeySet, promiseKeySet, signatureKeySet],
  );
  const typeKey = kycTypeField?.key;
  /**
   * 默认值取「居民身份证」，取不到就退到后台配的第一个选项 ——
   * 类型是必填项，不能让它空着把学员拦在提交那一步。
   */
  const typeDefault = useMemo(() => {
    const options = kycTypeField?.options ?? [];
    return (options.find((option) => option.value === RESIDENT_ID_TYPE) ?? options[0])?.value;
  }, [kycTypeField]);
  /** 按字段 key 取字符串值（核验页的四个值都从这里取，统一空值为 ''） */
  const text = useCallback(
    (key?: string) => {
      const raw = key ? answers?.[key] : undefined;
      return typeof raw === 'string' ? raw : '';
    },
    [answers],
  );
  /**
   * 是否要填收件地址：**只读**服务端建单时的 `deliveryMethod`（默认 MAIL）。
   * 学员端不提供切换入口 —— 真要改成现场领取，是后台在发放安排里做的事。
   */
  const needAddress = (detail.data?.deliveryMethod ?? 'MAIL') === 'MAIL';

  /**
   * 「复制单位地址」的来源字段（2026-11-01 用户要求）。
   *
   * 在职学员让教材寄到单位，就得把省 / 市 / 区 + 详细地址再选一遍、再打一遍。
   * 有单位地址就给他一键复制；**当前表单没有这个题就不显示按钮**（判定在
   * `lib/address-reuse.ts`，那里还挡住了「条件隐藏的字段」与「拿别的 region 凑数」）。
   *
   * 依赖 `answers`：字段是否可见由 `visibleWhen` 决定，答案变了按钮也要跟着变。
   */
  const workUnitField = useMemo(
    () => (schema.data ? findWorkUnitRegionField(schema.data.fields, answers ?? {}) : null),
    [schema.data, answers],
  );

  const copyWorkUnitAddress = useCallback(async () => {
    if (!workUnitField) return;
    const source = answers?.[workUnitField.key];

    if (!hasCopyableRegion(source)) {
      // 不把按钮禁掉：禁用态说不出「为什么点不了」，学员只会以为坏了
      Toast.show({ icon: 'fail', content: `${workUnitField.label}还没填，先在上面填好` });
      return;
    }
    if (addressHasContent(address)) {
      const confirmed = await Dialog.confirm({
        content: '邮寄地址已填写，复制会用单位地址覆盖当前内容，继续吗？',
        confirmText: '覆盖',
        cancelText: '取消',
      });
      if (!confirmed) return;
    }

    setAddress((prev) => ({ ...prev, ...regionToAddressPatch(source) }));
    // 上一次提交失败留下的「请选择完整省 / 市 / 区」红字要一并清掉，
    // 否则刚复制完地址下面还挂着旧报错
    setErrors((prev) => prev.filter((item) => !(item.key in EMPTY_ADDRESS)));
  }, [workUnitField, answers, address]);

  // ------------------------------------------------------------ 续填落点
  //
  // 一次「继续填写」要回答两个问题：**从哪一步开始**、**页面停在哪一块**。
  // 两者都不能猜得太远 —— 猜错落点会把学员送到一个他没填过的位置，
  // 猜错滚动位置只是多划一下。所以落点保守算，滚动位置按已有答案推。

  /**
   * 草稿的地址（同步可得的那一份）。
   *
   * 不读 `address` state：它要等一个 effect 才回填，首帧永远是空地址，
   * 会把「填过地址的草稿」误判成没填过。本地兜底 → 服务端草稿，与
   * 回填 effect 用的是同一份优先级，只是不排队等渲染。
   */
  const draftAddress = useMemo<AddressState>(() => {
    const loaded = detail.data;
    if (!loaded) return EMPTY_ADDRESS;
    return readLocalAddress(loaded.id) ?? loaded.address ?? EMPTY_ADDRESS;
  }, [detail.data]);

  /**
   * 第 1 步（基本信息）是否已经填齐 —— 含教材邮寄地址。
   *
   * 「齐了 ⇒ 上次一定走到更后面」是这个落点判断的唯一依据：第 2 步的核验卡
   * 只有在第 1 步校验通过后才能进入。反过来「没齐 ⇒ 就留在第 1 步」，
   * 也是必然的：学员上次就卡在这里。
   */
  const stepOneDone = useMemo(() => {
    if (!schema.data || !answers) return false;
    const fieldMissing = ownFieldErrors(fields, collectErrors(fields, answers)).filter(
      (error) =>
        !kycKeySet.has(error.key) &&
        !promiseKeySet.has(error.key) &&
        !signatureKeySet.has(error.key),
    );
    const addressMissing = needAddress ? validateAddress(draftAddress) : [];
    return fieldMissing.length === 0 && addressMissing.length === 0;
  }, [
    schema.data,
    answers,
    fields,
    kycKeySet,
    promiseKeySet,
    signatureKeySet,
    needAddress,
    draftAddress,
  ]);

  /** 落点：第 1 步齐了就直接去核验/上传那一屏，否则留在第 1 步 */
  const resumeStep = stepOneDone ? 1 : 0;

  /**
   * 滚动锚点：**渲染顺序里最后一个已经有值的字段**。
   *
   * 这就是「上次填到哪儿」最接近的同步证据 —— 学员离开时最后填的那一项，
   * 必然是当前有值字段里的最后一个。对真实草稿验证过：停在「报名材料 →
   * 学历证书」上传框上的单子，算出来正是 `educationCert`。
   *
   * 排序必须按**后台配的分组顺序 + 组内顺序**，不能按 `fields` 原序：
   * 组是 `basic → education → extra`，按原序会把「报名材料」排到中间去，
   * 算出个页面中部的位置，比不滚更让人困惑。
   */
  const resumeAnchorKey = useMemo(() => {
    if (!schema.data || !answers) return null;
    const ordered = [...(schema.data.groups ?? [])]
      .sort((a, b) => a.order - b.order)
      .flatMap((group) =>
        fields
          .filter((field) => field.group === group.key)
          .sort((a, b) => a.order - b.order)
          .filter(
            (field) =>
              !kycKeySet.has(field.key) &&
              !promiseKeySet.has(field.key) &&
              !signatureKeySet.has(field.key) &&
              isFieldShown(field, answers),
          ),
      );
    let last: string | null = null;
    for (const field of ordered) {
      const value = answers[field.key];
      const filled =
        typeof value === 'string'
          ? value.trim() !== ''
          : Array.isArray(value)
            ? value.length > 0
            : value != null;
      if (filled) last = field.key;
    }
    return last;
  }, [schema.data, fields, answers, kycKeySet, promiseKeySet, signatureKeySet]);

  /**
   * 待滚动的锚点。结算时写进来，由下一个 effect 在**表单真正挂载后**执行。
   *
   * 为什么拆成两步：结算那个 effect 里 `setResumed(true)` 会让 `resumed` 从
   * false 变 true，而 `resumed` 就在它自己的依赖里 —— effect 立刻重跑，
   * 上一次挂的 `cancelAnimationFrame` 清理函数把还没执行的滚动**取消掉**了。
   * 现象是「逻辑算对了但页面纹丝不动」，且只在真机长表单上看得出来。
   */
  const pendingAnchorRef = useRef<string | null>(null);

  /**
   * 结算一次续填落点（只算一次）。
   *
   * 必须等 `schema` 到位：`fields` / `groups` 都来自 Schema，没加载完时
   * `stepOneDone` 与 `resumeAnchorKey` 都会得出空结论 —— 提前结算等于白算。
   */
  useEffect(() => {
    if (resumed) return;
    if (!answers || schema.isPending || !schema.data) return;
    if (resumeStep > 0) {
      setStep(resumeStep);
      // 到过第 2 步 ⇒ 第 1 步必然通过过，步骤条要把这两格都放开
      setMaxStep((current) => Math.max(current, resumeStep));
    } else {
      pendingAnchorRef.current = resumeAnchorKey;
    }
    setResumed(true);
  }, [answers, schema.isPending, schema.data, resumeStep, resumeAnchorKey, resumed]);

  /**
   * 把页面滚到「上次填到的那一项」。
   *
   * 放到下一帧再滚：此刻表单刚挂载、上传缩略图与地区组件还没撑开高度，
   * 立即 `scrollIntoView` 会算出一个偏上的位置。`block:'center'` 而不是 `'start'`，
   * 是为了让固定页头不至于盖住目标那一行。找不到锚点（字段被条件隐藏）就不滚，
   * 停在页面顶部 —— 这也比滚到一个空位置好。
   */
  useEffect(() => {
    if (!resumed) return;
    const key = pendingAnchorRef.current;
    if (!key) return;
    pendingAnchorRef.current = null;
    const frame = requestAnimationFrame(() => {
      document.querySelector(`[data-field-key="${key}"]`)?.scrollIntoView({ block: 'center' });
    });
    return () => cancelAnimationFrame(frame);
  }, [resumed]);

  const payload = useMemo(() => (answers ? prunedAnswers(fields, answers) : {}), [answers, fields]);

  /**
   * 补齐后的完整收件地址（**这才是发给服务端的那一份**）。
   *
   * 报名的输入框里只有「省市区 + 详细地址」——收件人与手机号就是报名人姓名和
   * 注册手机号，没必要让学员再敲一遍（2026-10-21 用户裁决）。它们的来源是核验
   * 那一步已经采集的那两个字段，所以这里直接取 `answers`：
   *   - 姓名 / 手机号都已经有值 → 用新的（学员改过姓名也能跟上）；
   *   - 还没有（第 1 步阶段姓名还没填）→ 退回地址里存着的旧值，都没有就留空，
   *     这时 `validateAddressPayload` 会拦住不发请求，只落本地，等字段齐了自动补发。
   */
  const addressPayload = useMemo<AddressState>(
    () => ({
      ...address,
      receiverName:
        text(kycNameField?.key).trim() || kycNameFallback.trim() || address.receiverName,
      phone: text(kycPhoneField?.key).trim() || address.phone,
    }),
    [address, text, kycNameField, kycPhoneField, kycNameFallback],
  );

  /**
   * 自动保存·地址。
   *
   * 依赖里同时有 `address` 与 `addressPayload`：只依赖前者的话「学员改姓名」
   * 不会重新推一次，服务端就永远存着旧收件人。
   */
  useEffect(() => {
    if (!addressHydratedRef.current || !registrationId) return;
    // 本地只存地址本身（下次回填与这里读的是同一份形状）
    saveLocalAddress(registrationId, address);
    if (needAddress) addressQueue?.push(addressPayload);
  }, [address, addressPayload, needAddress, registrationId, addressQueue]);

  /**
   * 证件类型：一期**固定居民身份证、不给选**（线上链只认它 —— OCR 只认身份证，
   * 证件号校验是 18 位 + GB 11643 校验位）。所以这里不是"补默认值"，而是"归位"：
   * 历史草稿里若存着别的类型（那时候还能选），也一并改回居民身份证，
   * 否则会留下一个改不掉、又过不了校验的字段。
   */
  useEffect(() => {
    if (!typeKey || !typeDefault) return;
    setAnswers((prev) => {
      if (!prev) return prev;
      if (prev[typeKey] === typeDefault) return prev;
      return { ...prev, [typeKey]: typeDefault };
    });
  }, [typeKey, typeDefault]);

  /**
   * 自动保存·表单。分两层写，顺序不能反：
   *   ① **本地即时**（同步、零延迟）—— 断网 / 切后台 / 进程被回收都不丢；
   *   ② **服务端节流**（停手 1.5s 一次）—— 让草稿跨会话、跨设备可恢复。
   *
   * 首次进入只登记基线、不发请求（否则每打开一次表单就白 `version+1`）。
   * schema 没到之前不算首次：那时 `payload` 还是空对象，拿它当基线会立刻多存一次。
   */
  useEffect(() => {
    if (!answers || !registrationId) return;
    if (!formBaselineRef.current) {
      if (fields.length === 0) return;
      formBaselineRef.current = true;
      formQueue?.markSaved(payload);
      return;
    }
    saveLocalDraft(registrationId, answers);
    formQueue?.push(payload);
  }, [answers, payload, fields.length, registrationId, formQueue, saveLocalDraft]);

  /**
   * 地址回填：本地兜底 → 服务端草稿 → 空。
   *
   * 顺序与表单相反（这里本地优先）：本地那份是「填了但还没同步上去」的，
   * 服务端那份是「上次同步成功」的，前者更新。回填这一下用 `markSaved` 挡掉，
   * 否则一进页面就会把刚读到的地址原样写回服务端。
   */
  useEffect(() => {
    const loaded = detail.data;
    if (!loaded || addressHydratedRef.current) return;
    addressHydratedRef.current = true;
    const restored = readLocalAddress(loaded.id) ?? loaded.address ?? EMPTY_ADDRESS;
    setAddress(restored);
    addressQueue?.markSaved(restored);
  }, [detail.data, addressQueue]);

  /** 手动立即保存（保存状态变红时点「重试」；也用于切步骤、离开页面前的强制落盘） */
  const flushNow = useCallback((): Promise<void> => {
    if (!registrationId || !answers) return Promise.resolve();
    // 先保证本地有：即使这次网络失败，内容也还在本机
    saveLocalDraft(registrationId, answers);
    if (needAddress) saveLocalAddress(registrationId, address);
    return Promise.all([
      formQueue?.flush() ?? Promise.resolve(),
      needAddress ? (addressQueue?.flush() ?? Promise.resolve()) : Promise.resolve(),
    ]).then(() => undefined);
  }, [registrationId, answers, address, needAddress, formQueue, addressQueue, saveLocalDraft]);

  /**
   * 离开表单前的确认（2026-10-26 真机反馈）。
   * ---------------------------------------------------------------------------
   * 原行为：第 1 步页头返回走默认的 `navigate(-1)`，一路退到服务台首页；
   * 全面屏左缘右滑同理。学员填了半页、手一滑就没了 —— 而草稿其实一直在自动保存，
   * 他并不知道「还能回去接着填」，于是重填一遍。
   *
   * 问的是「退出还是继续」，**不是**「要不要保存」：自动保存（`lib/autosave`）
   * 已经把内容存上了，再问一句「是否保存」会让人以为没存上。这里只做两件事：
   *   ① 先 `flush` 一次 —— 最后一次输入可能还在 1.5s 静默期里，不落盘就真丢了；
   *   ② 让学员确认这一步是「走」还是「误触」。
   *
   * ⚠️ **守卫必须同步返回 `stay`，不能 await 用户点按钮**（2026-10-26 实测修正）：
   * 让 `onBack` 一直悬着等确认框，`settle` 就永远不会补回哨兵 —— 而这次返回已经把
   * 历史里那条哨兵吃掉了。此时用户再滑一下，`popstate` 直接落到**上一页**，
   * 人在确认框还开着的情况下被弹走（实测就是这么丢的）。
   *
   * 所以改成：先补哨兵（`stay`），再在后台问；用户点「退出」时才真正退格（`leaveRef`）。
   * 副作用是「开着框再滑一次」只会落回原地，不会叠出第二个框。
   */
  const exitAskingRef = useRef(false);
  /** 手势返回 / 页头返回共用一个入口，避免两处各写一份确认逻辑 */
  const askExitRef = useRef<() => void>(() => undefined);
  /** 点「退出」后真正离开：退两格（哨兵 + 本页），详见下方 effect 里的注释 */
  const leaveRef = useRef<() => void>(() => undefined);

  const askExit = useCallback(() => {
    if (exitAskingRef.current) return;
    exitAskingRef.current = true;
    // `flush` 可能要几百毫秒（还带上一次静默期的内容），先把框压住，
    // 免得这段时间里连点两次返回又开一个。
    void flushNow().then(() => {
      Dialog.confirm({
        title: '退出填写？',
        content: '已填的内容会自动保存，下次从「报名列表」进入可以接着填。',
        confirmText: '退出',
        cancelText: '继续填写',
        onConfirm: () => leaveRef.current(),
        // `onClose` 在确认/取消之后还会再触发一次，这里只负责复位，重复无害
        onCancel: () => {
          exitAskingRef.current = false;
        },
        onClose: () => {
          exitAskingRef.current = false;
        },
      });
    });
  }, [flushNow]);

  /**
   * 守卫要读最新的 `askExit`，但**不能**把它写进 effect 依赖 ——
   * `askExit` 依赖 `flushNow` → 依赖 `answers` / `address`，每敲一个字都会变，
   * effect 就会重跑一次 `pushBackGuard`，历史里堆一串同 URL 的哨兵。
   */
  useEffect(() => {
    askExitRef.current = askExit;
  }, [askExit]);

  /**
   * **真正进入编辑态**才拦返回。
   *
   * 加载中 / 报错 / 已提交跳详情 / 须知屏都不拦 —— 那些状态下没有「填写进度」
   * 可丢，弹一个「退出填写？」反而莫名其妙。
   */
  const editing =
    !detail.isPending &&
    !detail.error &&
    !!detail.data &&
    // 只在「真能编辑」的三种情形下拦返回：草稿 / 驳回单 / 补交单。
    // 补交单的状态还是 `PENDING_PAYMENT`（判定不改状态），只能靠放行与缺项认出来
    (detail.data.status === 'DRAFT' ||
      detail.data.status === 'REJECTED' ||
      detail.data.grantedFields.length > 0 ||
      detail.data.supplementMissingItems.length > 0) &&
    !schema.isPending &&
    !schema.error &&
    !!schema.data &&
    resumed &&
    (consent || consentDone);

  /**
   * 手势返回（全面屏左缘右滑 / 安卓返回键 / 微信返回）→ 补哨兵 + 弹确认。
   *
   * `release()` **不带 `dropSentinel`**（卸载那一路）：这层守卫卸载的原因通常是*已经*
   * 跳到新页面了（提交成功 → 报名成功页），此时弹历史会把新页面顶掉。
   * 代价是提交成功后历史里会留一条同 URL 的记录，但那一格落回来时
   * `EnrollForm` 会因为状态已非 `DRAFT` 立刻 `replace` 到详情页，用户看不到。
   *
   * 点「退出」那一路（`leaveRef`）必须**带 `dropSentinel`**：哨兵还压着，
   * 不弹掉就只是关掉确认框、人还停在表单上。
   */
  useEffect(() => {
    if (!editing) return;
    const guard = pushBackGuard(() => {
      askExitRef.current();
      return 'stay';
    });
    /**
     * 点「退出」：`stay` 已经把哨兵补回去了，所以要退**两格**（哨兵 + 本页）。
     * `release(true)` 只退一格，那只弹掉哨兵 —— 人还停在表单上。
     */
    leaveRef.current = () => {
      exitAskingRef.current = false;
      leaveTopGuard();
    };
    return () => {
      leaveRef.current = () => undefined;
      guard.release();
    };
  }, [editing]);

  /**
   * 页头返回键：问栈顶守卫，不自己 `navigate(-1)`。
   *
   * 守卫压了一条同 URL 的哨兵，`navigate(-1)` 只会把它弹掉（页面停在原地，
   * 看起来「点了没反应」）。`triggerTopGuard` 会顺手把历史挪到位。
   * 返回 false = 当前没挂守卫（理论上走不到，主分支必然在编辑态），兜底直接退。
   */
  const requestExit = useCallback(() => {
    if (triggerTopGuard()) return;
    navigate(-1);
  }, [navigate]);

  /**
   * 进到某一步：同时推进「已到达过的最远步骤」。
   * 步骤条的可点性只认这个值 —— 见 `maxStep` 的注释。
   */
  const goStep = useCallback((next: number) => {
    setStep(next);
    setMaxStep((current) => Math.max(current, next));
  }, []);

  /**
   * 点步骤条跳转：落盘 + 切页，**不重跑校验**。
   *
   * 能点到的都是到过的步骤，那会儿校验已经过；再跑一遍会把「后来被清空的必填项」
   * 当场拦住，而学员的本意只是回去看一眼别的地方。
   */
  const onSelectStep = useCallback(
    (next: number) => {
      void flushNow();
      setStep(next);
    },
    [flushNow],
  );

  const goNextFromForm = useCallback(() => {
    if (!answers) return;
    /**
     * 校验用**全量字段**跑、只丢掉「不归这一步管」的结论。
     * 不能直接传本步字段：`validateAnswers` 会把「答案里有、字段表里没有」的
     * key 判成 `表单中不存在该字段` —— 回填过草稿的学员（答案里已有姓名 / 手机号 /
     * 证件号）就会被这一句卡死在第 1 步，连错误提示都指不到具体输入框。
     * `ownFieldErrors` 就是这一步的过滤器，脏键的清除在 `knownKeySet` 那个 effect 里。
     */
    const found = ownFieldErrors(fields, collectErrors(fields, answers)).filter(
      (error) =>
        !kycKeySet.has(error.key) &&
        !promiseKeySet.has(error.key) &&
        !signatureKeySet.has(error.key),
    );
    /**
     * 教材邮寄地址属于第 1 步，但它**不在这份表单里**（走独立端点），所以单独校验。
     * 只查地址本身：收件人 / 手机号报名阶段不问（领取教材时自动带入）。
     */
    const addressErrors = needAddress ? validateAddress(address) : [];
    const all = [...found, ...addressErrors];
    setErrors(all);
    if (all.length > 0) {
      Toast.show({ icon: 'fail', content: all[0]?.message ?? '请检查表单填写' });
      return;
    }
    // 切步骤前强制落盘一次（不等 1.5s 静默期）—— 学员很可能在下一步停留很久
    void flushNow();
    goStep(1);
  }, [
    answers,
    fields,
    kycKeySet,
    promiseKeySet,
    signatureKeySet,
    needAddress,
    address,
    flushNow,
    goStep,
  ]);

  /**
   * 第 2 步 → 第 3 步：门禁全部收在 `KycFlow.submit()` 里。
   * 父级不用 `kycPassed` 提前短路 —— 否则「准入已通过、但证件号后来被改」会被放行
   * （本地布尔值不知道号码变了，核验页的 `checkedFor` 才知道）。
   */
  const goNextFromKyc = useCallback(() => {
    kycRef.current?.submit();
  }, []);

  const doSubmit = useCallback(async () => {
    if (!registrationId || !answers) return;
    /**
     * 错误按**归属步骤**分流：承诺 / 签名的必填错误不能把人送回第 1 步，
     * 核验字段的错误也不能送回第 2 步 —— 那些页面里根本没有对应的输入框，
     * 学员只会看到一句红色 toast 和一张挑不出毛病的表。
     */
    const allErrors = collectErrors(fields, answers);
    const kycErrors = allErrors.filter((error) => kycKeySet.has(error.key));
    const signErrors = allErrors.filter(
      (error) => promiseKeySet.has(error.key) || signatureKeySet.has(error.key),
    );
    const baseErrors = ownFieldErrors(fields, allErrors).filter(
      (error) =>
        !kycKeySet.has(error.key) &&
        !promiseKeySet.has(error.key) &&
        !signatureKeySet.has(error.key),
    );
    if (baseErrors.length > 0) {
      setErrors(baseErrors);
      setStep(0);
      Toast.show({ icon: 'fail', content: baseErrors[0]?.message ?? '基本信息填写有误' });
      return;
    }
    if (kycErrors.length > 0) {
      setErrors(kycErrors);
      setStep(1);
      Toast.show({ icon: 'fail', content: kycErrors[0]?.message ?? '请完成身份核验' });
      return;
    }
    if (!kycPassed) {
      setStep(1);
      Toast.show({ icon: 'fail', content: '请先完成身份核验' });
      return;
    }
    // 承诺 + 签名就在本页：错误就地显示，不切步骤（切走反而看不到哪条没勾）
    if (signErrors.length > 0) {
      setErrors(signErrors);
      Toast.show({
        icon: 'fail',
        content: signErrors[0]?.message ?? '请勾选承诺并手写签名',
      });
      return;
    }

    // 收件地址是**寄教材用的资料**，不是「领取方式」的附属品：
    // 领取方式由后台按发放安排定（一期以邮寄为主，现场领取只做台账），
    // 学员这边唯一要做的就是把地址填对。提交前再兜底查一次 ——
    // 从确认页退回第 1 步清空地址后仍可能走到这里。
    const addressErrors = needAddress ? validateAddressPayload(addressPayload) : [];
    if (addressErrors.length > 0) {
      setErrors(addressErrors);
      /**
       * 按错误归属分流：`receiverName` / `phone` 的入口在**第 2 步核验卡**
       * （收件人与手机号自动带入，第 1 步没有这两个输入框）——送回第 0 步
       * 等于让人站在一张挑不出毛病的表前死循环；只有省市区 / 详细地址
       * 这类真正属于第 1 步的错误才送回去。
       */
      const missingIdentity = addressErrors.some(
        (item) => item.key === 'receiverName' || item.key === 'phone',
      );
      if (missingIdentity) {
        setStep(1);
        Toast.show({
          icon: 'fail',
          content: '请先完成身份核验（收件人与手机号会自动带入）',
        });
      } else {
        setStep(0);
        Toast.show({ icon: 'fail', content: addressErrors[0]?.message ?? '请完善教材邮寄地址' });
      }
      return;
    }

    setBusy(true);
    setErrors([]);
    try {
      // ① 地址（服务端已按 MAIL 建单；若是 PICKUP 则没有地址这回事）
      if (needAddress) await saveAddress(registrationId, addressPayload);
      // ② 表单定稿：**显式保存**而不是 flush 队列 —— 自动保存吞掉异常（不打断输入），
      //    而提交前这一份必须是成功的，失败就得让学员看到错误、留在本页。
      await saveDraft(registrationId, payload);
      // ③ 提交（幂等令牌由 request 层生成）
      const submitted = await submitRegistration(registrationId);
      // 队列里可能还压着一次 1.5s 后的保存：登记为已存，别在提交后再打一次
      // （那时单据已离开可编辑状态，请求只会换回一个无意义的失败）
      formQueue?.markSaved(payload);
      addressQueue?.markSaved(addressPayload);
      clearLocalDraft(registrationId);
      rememberRegistration(submitted.id);
      navigate(`/enroll/success?registrationId=${submitted.id}`, { replace: true });
    } catch (error) {
      if (error instanceof ApiError) {
        if (error.code === 'ENROLL-004') {
          // 服务端返回的字段错误同样按归属步骤送回：哪个字段错了就回哪一页
          const found = fieldErrorsOf(error);
          setErrors(found);
          setStep(stepOfKey(found[0]?.key));
        } else {
          const kycStep = kycStepOf(error);
          if (kycStep) {
            setKycRedo({ step: kycStep, notice: describeApiError(error) });
            setStep(1);
          }
        }
      }
      Toast.show({ icon: 'fail', content: describeApiError(error) });
    } finally {
      setBusy(false);
    }
  }, [
    registrationId,
    answers,
    fields,
    kycKeySet,
    promiseKeySet,
    signatureKeySet,
    kycPassed,
    needAddress,
    addressPayload,
    payload,
    clearLocalDraft,
    navigate,
    stepOfKey,
    formQueue,
    addressQueue,
  ]);

  /**
   * 页头显示的状态 = 两条链路里**更差**的那个。
   *
   * 优先级 `saving > error > pending > saved`：任一条还在存就别显示「已保存」，
   * 任一条失败就必须让人看见 —— 否则他会以为存上了，然后在提交时才发现内容不对。
   */
  const saveIndicator = useMemo((): { state: SaveState; at: number | null } => {
    const rank: Record<SaveState, number> = {
      idle: 0,
      saved: 1,
      pending: 2,
      saving: 3,
      error: 4,
    };
    return rank[formSave.state] >= rank[addressSave.state] ? formSave : addressSave;
  }, [formSave, addressSave]);

  /** 状态变红时点一下：立刻重试，不等下一次输入 */
  const onRetry = useCallback(() => {
    void flushNow();
  }, [flushNow]);

  /**
   * 回上一步。回去之前强制落盘 —— 上一步很可能被改成另一个值，
   * 「点返回 = 放弃这次修改」不是这里该有的语义。
   */
  const goBack = useCallback(() => {
    void flushNow();
    setStep((current) => Math.max(0, current - 1));
  }, [flushNow]);

  /**
   * 页面被隐藏 / 卸载时落盘。
   *
   * 移动端 `beforeunload` 基本不触发（切后台、从微信退出都不会走它），
   * 所以两条都挂：`visibilitychange → hidden` 覆盖切后台，
   * `pagehide` 覆盖真正的离开。本地那一层已经即时写过，这里只是尽力把服务端补上。
   */
  useEffect(() => {
    const flush = () => {
      void flushNow();
    };
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') flush();
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', flush);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', flush);
    };
  }, [flushNow]);

  if (detail.isPending) {
    return (
      <PageShell title="填写报名信息" back>
        <LoadingBlock text="正在加载报名信息" />
      </PageShell>
    );
  }
  if (detail.error || !detail.data) {
    return (
      <PageShell title="填写报名信息" back>
        <ErrorBlock
          message={detail.error instanceof Error ? detail.error.message : '报名单不存在'}
          onRetry={() => {
            void detail.refetch();
          }}
        />
      </PageShell>
    );
  }

  // 已提交的单不再进入编辑态（驳回单除外，它需要修改后重提）
  //
  // 补交态（2026-10-27）也必须放行：`judgeSupplement` **不改状态**，被判补交的单子
  // 可能还停在 `PENDING_PAYMENT`。没有这条例外，详情页的「补充资料」按钮点进来
  // 只会被立刻弹回详情页 —— 学员看不到任何可改的东西。
  const hasSupplement =
    detail.data.supplementMissingItems.length > 0 || detail.data.grantedFields.length > 0;
  if (detail.data.status !== 'DRAFT' && detail.data.status !== 'REJECTED' && !hasSupplement) {
    return <Navigate to={`/registration/${detail.data.id}`} replace />;
  }
  /**
   * 字段编辑分级：**只有草稿不分级**。
   *
   * 原先只在驳回态分级（补交态漏了），后果是补交时 `review` 字段在界面上照样能打字，
   * 一保存就被服务端 `409 ENROLL-008` 顶回来 —— 学员把整页重填一遍才发现改不了。
   */
  const graded = detail.data.status !== 'DRAFT';
  /** 本次已获准修改的字段（服务端已按有效期过滤，过期即为空） */
  const grantedKeys = detail.data.grantedFields;

  if (schema.isPending || !answers) {
    return (
      <PageShell title="填写报名信息" back>
        <LoadingBlock text="正在加载表单" />
      </PageShell>
    );
  }
  if (schema.error || !schema.data) {
    return (
      <PageShell title="填写报名信息" back>
        <ErrorBlock
          message={schema.error instanceof Error ? schema.error.message : '表单配置加载失败'}
          onRetry={() => {
            void schema.refetch();
          }}
        />
      </PageShell>
    );
  }
  /**
   * 续填落点还没结算完。
   *
   * 必须放在 Schema 的错误分支**之后**：Schema 拉失败时上面那条 effect 会
   * 一直早返回（拿不到字段表就不该算落点），若把 `!resumed` 并进第一个判断，
   * 这里就会永远转圈，把真正的「表单配置加载失败」盖掉。
   */
  if (!resumed) {
    return (
      <PageShell title="填写报名信息" back>
        <LoadingBlock text="正在加载表单" />
      </PageShell>
    );
  }

  /**
   * 进入表单前的一屏：须知与授权（不属于步骤条里的 3 步）。
   *
   * 为什么不放在第 1 步页脚：第 2 步要拍身份证与人脸，授权必须**早于采集**；
   * 为什么不放最后一步：那是采集完才补授权，顺序反了。
   * 也没有做「点《报名须知》跳条款页」的假链接 —— 一期没有条款页，
   * 正文就摆在当前这一屏，看得见、读得到、才谈得上「已阅读并同意」。
   *
   * **续填不再重复这一屏**：`consentDone` 为真（草稿有内容，或驳回单）说明学员
   * 早就点过「同意并开始填写」了。每次回来都强制再读一遍，只会让「继续填写」
   * 看起来像「重新开始」—— 授权已经给过，就不该拿它当路障。
   */
  if (!consent && !consentDone) {
    return (
      <PageShell
        title="报名须知与授权"
        back
        layout="form"
        footerRows={2}
        footer={
          <>
            <label className="agree">
              <input
                type="checkbox"
                className="agree__box"
                checked={agreed}
                onChange={(event) => setAgreed(event.target.checked)}
              />
              <span className="agree__text">
                我已阅读并同意上述《报名须知》与《个人信息授权书》
              </span>
            </label>
            <Button
              variant="primary"
              className="btn--hero"
              block
              disabled={!agreed}
              onClick={() => setConsent(true)}
            >
              同意并开始填写
            </Button>
          </>
        }
      >
        <Card title="报名须知" className="consent-card">
          <NoticeList items={ENROLL_NOTICE} />
        </Card>
        <Card title="个人信息授权书" className="consent-card">
          <NoticeList items={PRIVACY_NOTICE} />
        </Card>
        <Notice tone="info">如不同意上述任一条款，请勿继续填写，可联系客服咨询。</Notice>
      </PageShell>
    );
  }

  return (
    <PageShell
      title={PAGE_TITLE}
      back
      layout="form"
      // 页脚只剩一颗按钮（须知勾选已挪到前置屏），所以按 1 排留白，
      // 否则内容区底部会多出 60px 空白
      footerRows={1}
      // 页头右侧从「存草稿」换成**保存状态**：自动保存之后「要不要存」不再需要
      // 学员决策，他需要知道的是「存上没有」。三步都显示 —— 每一步都有会被改动的
      // 字段（核验回填的姓名 / 手机号 / 证件号，第 1 步的地址）。
      // 第 1 步的返回是「退出表单」，要过确认；2/3 步是「回上一步」，直接切
      onBack={step === 0 ? requestExit : goBack}
      right={<SaveIndicator state={saveIndicator.state} at={saveIndicator.at} onRetry={onRetry} />}
      footer={
        step === 0 ? (
          <Button variant="primary" className="btn--hero" block onClick={goNextFromForm}>
            下一步
          </Button>
        ) : (
          /**
           * 第 2/3 步页脚两键：左「上一步」右「下一步 / 提交」。
           * 加这颗按钮不是因为页头返回不好使，而是**页头那颗长得像「退出」**——
           * 学员不敢点，于是就变成「回不去改」。步骤条也能点，两条路互补。
           */
          <div className="btn-row">
            <Button variant="ghost" onClick={goBack}>
              上一步
            </Button>
            {step === 1 ? (
              <Button variant="primary" onClick={goNextFromKyc}>
                下一步
              </Button>
            ) : (
              /* 补交态**没有「提交」**：`submit` 只接受 DRAFT / REJECTED，
                 补交单还停在 PENDING_PAYMENT，点提交什么都不会发生（服务端
                 走 `loadOwnView` 原样返回，既不报错也不落库 —— 最坏的一种静默）。
                 这里只做「立刻落盘 + 回详情」，让学员看得见改动已经生效。 */
              <Button
                variant="primary"
                loading={busy}
                onClick={() => {
                  if (hasSupplement) {
                    void flushNow().then(() => {
                      navigate(`/registration/${detail.data.id}`, { replace: true });
                    });
                    return;
                  }
                  void doSubmit();
                }}
              >
                {hasSupplement ? '保存并返回详情' : '提交报名'}
              </Button>
            )}
          </div>
        )
      }
    >
      <StepDots steps={FORM_STEPS} current={step} maxReached={maxStep} onSelect={onSelectStep} />

      {detail.data.status === 'REJECTED' ? (
        <Notice tone="danger">
          该报名单已被驳回（第 {detail.data.rejectCount} 次），请按驳回原因修改后重新提交。
        </Notice>
      ) : null}

      {step === 0 ? (
        <>
          {/*
            第 1 步 = **全部字段** − 核验字段 − 承诺 − 签名。

            不再传 `onlyGroupKeys`：按后台分组切页时，「材料与承诺」那一组把
            「交材料」和「签个字」捆成了一个步骤，拆也拆不干净（同组里还混着备注）。
            现在按「谁来产出这个值」切 —— 只有需要举手机 / 收短信的才去第 2 步。

            卡片标题沿用后台组标签（基本信息 / 报考与职业信息 / 报名材料），
            所以后台改组名就能改标题，前端不写死。
          */}
          <FormRenderer
            schema={schema.data}
            value={answers}
            onChange={setAnswers}
            errors={errors}
            graded={graded}
            grantedKeys={grantedKeys}
            omitKeys={stepOneOmitKeys}
            registrationId={detail.data.id}
          />

          {/*
            教材邮寄地址（2026-10-21 从确认页搬来）。

            只问「寄到哪儿」：收件人与手机号就是报名人姓名 + 注册手机号，在
            **领取教材**时自动带入、可改。报名阶段再问一遍，等于让同一个人把同一份
            信息写两次，两次不一致时以哪份为准也没有答案。

            标题仍写**教材邮寄地址**而不是「收件地址」：上面「报考与职业信息」里
            有一个「单位所在地」（`unitRegion`，单位在哪，非必填），两处都叫"地址"
            会让人以为重复采集。
          */}
          {needAddress ? (
            <Card
              title="教材邮寄地址"
              action={
                workUnitField ? (
                  <Button variant="ghost" size="mini" onClick={() => void copyWorkUnitAddress()}>
                    复制单位地址
                  </Button>
                ) : undefined
              }
            >
              <p className="field__help">教材寄出前可随时修改；领取教材时再确认收件人与手机号</p>
              {/* 区域选**码**（D-05）：复用表单 `region` 字段的控件，三级联动选到码，
                  详细地址也在该控件内 —— 与服务端存的行政区划码同一口径。 */}
              <div className="field">
                <span className="field__label">
                  所在地区<em className="field__required">*</em>
                </span>
                <span className="field__control">
                  <RegionField
                    value={address}
                    onChange={(next) => setAddress((prev) => ({ ...prev, ...next }))}
                    onBlur={() => undefined}
                  />
                </span>
              </div>
              {errors
                .filter((item) => item.key in EMPTY_ADDRESS)
                .map((item) => (
                  <div key={`err-${item.key}`} className="field-error">
                    {item.message}
                  </div>
                ))}
            </Card>
          ) : (
            <Notice tone="info">本期教材为现场领取，无需填写邮寄地址。</Notice>
          )}
        </>
      ) : null}

      {step === 1 ? (
        kycIdField && kycPhoneField ? (
          <KycFlow
            ref={kycRef}
            key={kycRedo?.step ?? 'fresh'}
            registrationId={detail.data.id}
            initialStep={kycRedo?.step}
            initialNotice={kycRedo?.notice}
            nameField={kycNameField ?? undefined}
            idField={kycIdField}
            typeField={kycTypeField ?? undefined}
            genderField={kycGenderField ?? undefined}
            ethnicityField={kycEthnicityField ?? undefined}
            phoneField={kycPhoneField}
            values={{
              // 姓名兜底：没配姓名字段时从 `kycNameFallback` 取，否则输入框受控值恒为空、打不进字
              name: text(kycNameField?.key) || kycNameFallback,
              idType: text(kycTypeField?.key),
              idCardNo: text(kycIdField.key),
              gender: text(kycGenderField?.key),
              ethnicity: text(kycEthnicityField?.key),
              phone: text(kycPhoneField.key),
            }}
            graded={graded}
            grantedKeys={grantedKeys}
            // 已经通过过核验（从确认页退回来 / 驳回重提）就别让人重拍证件照；
            // 被打回（kycRedo）时则为 false，正好强制重做。
            alreadyPassed={kycPassed && !kycRedo}
            onChange={(patch) => {
              // 姓名没有落点字段时不能丢弃：核验卡输入框与收件人姓名都靠它
              if (patch.name !== undefined && !kycNameField) setKycNameFallback(patch.name);
              setAnswers((prev) => {
                const next = { ...(prev ?? {}) };
                if (patch.name !== undefined && kycNameField) next[kycNameField.key] = patch.name;
                if (patch.idType !== undefined && kycTypeField)
                  next[kycTypeField.key] = patch.idType;
                if (patch.idCardNo !== undefined) next[kycIdField.key] = patch.idCardNo;
                if (patch.gender !== undefined && kycGenderField)
                  next[kycGenderField.key] = patch.gender;
                if (patch.ethnicity !== undefined && kycEthnicityField)
                  next[kycEthnicityField.key] = patch.ethnicity;
                if (patch.phone !== undefined) next[kycPhoneField.key] = patch.phone;
                return next;
              });
            }}
            onPassed={() => {
              setKycPassed(true);
              goStep(2);
            }}
          />
        ) : (
          <Notice tone="warning">
            当前表单缺少「身份证 / 手机号」字段配置，无法完成身份核验，请联系客服确认。
          </Notice>
        )
      ) : null}

      {step === 2 ? (
        <>
          {/*
            确认页只做三件事：**核对 → 勾选 → 签名**。
            2026-10-21 用户裁决把两样东西挪走了：
              - 教材邮寄地址 → 第 1 步（报名阶段只填地址本身）；
              - 费用明细 → 缴费功能（报名是纯信息填写，金额只在缴费页出现）。
            页面上再挂一份「这个多少钱」，等于让学员在还没提交的时候就先被金额牵着走，
            而报名根本还没到缴费那一步。

            汇总**不折叠**、也不分页：这一页的价值就是「一眼看完」，藏进折叠面板里
            等于没有。字段多时靠卡片分组（基本信息 / 报考与职业信息 / 报名材料）分隔。
          */}
          <Notice tone="info">请核对以下信息，确认无误后勾选承诺并手写签名。</Notice>

          <AnswerSummary
            groups={summaryGroups}
            answers={answers ?? {}}
            photoUrl={kycStepsQuery.data?.photoUrl ?? null}
          />

          <PromiseList
            fields={promiseFields}
            answers={answers ?? {}}
            onChange={(next) =>
              // 逐项合并：承诺是「签署」而不是「录入」，不能因为勾了一条就把同一份
              // 答案里别的字段一起覆盖掉（`next` 已经是展开后的整份答案）
              setAnswers((prev) => ({ ...(prev ?? {}), ...next }))
            }
            errors={errors}
          />

          {signatureFields.map((field) => (
            <SignatureBlock
              key={field.key}
              field={field}
              answers={answers ?? {}}
              /**
               * 存档签名图：答案里的值只是 `assetId`，画不出东西。切步骤会卸载重挂
               * `SignatureCanvas`，只有服务端这张图能让学员继续看到「签的是什么」。
               * 取不到（链接缺失 / 过期）时给 null，组件退回「已签名」文案。
               */
              signatureUrl={detail.data.signatureUrls?.[field.key] ?? null}
              registrationId={detail.data.id}
              onChange={(next) => setAnswers((prev) => ({ ...(prev ?? {}), ...next }))}
              /**
               * 签名落库是异步的（上传 → 拿 assetId），失焦时要**把新值显式传进去**。
               * 只在 `onBlur` 里读 state 会读到上传前的旧值，当场报「签名不能为空」。
               */
              onBlur={(target, next) => {
                const found = collectFieldErrors(target, next);
                setErrors((prev) => [...prev.filter((item) => item.key !== target.key), ...found]);
              }}
              errors={errors}
            />
          ))}

          <Notice tone="info">
            提交后将进入待缴费，请在截止时间前按缴费指引转账并填写备注码。
          </Notice>
        </>
      ) : null}
    </PageShell>
  );
}
